/**
 * Sugestão de correção de MOVIMENTAÇÃO (`app_aplicacao_ativo`) — resolução e patch.
 *
 * O caso real que motiva: a cotização detecta PU absurdo numa movimentação lançada (PU de
 * R$ 33,33 num dia em que o mercado estava a R$ 1.473,08) e a IA propõe o valor de mercado.
 *
 * ★ A sugestão NÃO diz qual ativo é. O `id_entidade_alvo` é a PK de `app_aplicacao_ativo` — a
 * movimentação, não o ativo — e o `racional` também não nomeia (só cita o id da movimentação).
 * Sem resolver, a fila mostra "Valor unitario: 1473.080768" e o assessor não tem como saber em
 * que ativo está o erro. O de-para vem da lista de movimentações do cliente, que já traz
 * `nome_ativo`, `operacao`, `data` e `id_ativo_cliente` na mesma resposta.
 *
 * ★ O ÍNDICE guarda o ATIVO junto da movimentação. O modal de movimentação resolve a moeda por
 * `edit.ativo.moeda` e cai em `isOffshore` quando ela falta — e a linha da movimentação NÃO tem
 * nenhum dos dois. Passar a movimentação no lugar do ativo fazia um lançamento em USD abrir com
 * `R$` no preço unitário. O payload é agrupado POR ATIVO (`{ <id>: { data, movements } }`), então
 * o ativo vem na mesma resposta: é só não jogar fora ao achatar.
 *
 * ★ A allowlist espelha `MOVIMENTACAO_CAMPOS_EDITAVEIS` do writer (`valor`, `quantidade`,
 * `valor_unitario`) — campo fora dela é ignorado pelo aceite, e deixá-lo entrar no modal faria o
 * assessor ver uma proposta que nunca seria gravada. `id_cliente`, `id_ativo_cliente`, `id_conta`
 * e `resgate` nunca entram: trocar o ativo ou o sentido da operação não é "corrigir cadastro".
 */
export const CAMPOS_ACEITOS_NA_MOVIMENTACAO = ['valor', 'quantidade', 'valor_unitario']

export const patchDaSugestaoMov = (suggestion) => {
  const dados = (suggestion && suggestion.dados) || {}
  return CAMPOS_ACEITOS_NA_MOVIMENTACAO.reduce((patch, campo) => {
    if (dados[campo] !== undefined && dados[campo] !== null) patch[campo] = dados[campo]
    return patch
  }, {})
}

/**
 * Patch do aceite montado a partir do que foi REALMENTE salvo no formulario — o assessor pode ter
 * mudado o valor antes de gravar, e a fila tem que registrar o que ficou, nao o que a IA propos.
 * Espelha `patchDoFormulario` do cadastro. Só os 3 campos que o writer de movimentacao aceita.
 */
export const patchSalvoDaMovimentacao = (data) => {
  const salvo = data || {}
  return CAMPOS_ACEITOS_NA_MOVIMENTACAO.reduce((patch, campo) => {
    if (salvo[campo] !== undefined && salvo[campo] !== null) patch[campo] = salvo[campo]
    return patch
  }, {})
}

export const dadosDoAceiteMov = (suggestion) => {
  const patch = patchDaSugestaoMov(suggestion)
  return Object.keys(patch).length ? patch : null
}

/**
 * A resposta de movimentações vem AGRUPADA por ativo, e o formato do agrupamento não é contrato
 * estável (já mudou de dict para lista antes). Por isso o índice é montado por varredura: qualquer
 * objeto que tenha `id_aplicacao` é uma movimentação, esteja em que nível estiver.
 */
export const indexarMovimentacoes = (payload) => {
  const indice = {}
  const visitados = new Set()
  const cavar = (no, ativo) => {
    if (!no || typeof no !== 'object') return
    if (visitados.has(no)) return
    visitados.add(no)
    if (Array.isArray(no)) {
      no.forEach((item) => cavar(item, ativo))
      return
    }
    if (no.id_aplicacao !== undefined && no.id_aplicacao !== null) {
      indice[String(no.id_aplicacao)] = { movimentacao: no, ativo: ativo || null }
    }
    const doGrupo = no.data && no.movements ? no.data : ativo
    Object.keys(no).forEach((k) => cavar(no[k], doGrupo))
  }
  cavar(payload, null)
  return indice
}

/**
 * O payload é chaveado pelo ID DO ATIVO (`{ <id_ativo_cliente>: { data, movements } }`), então a
 * mesma resposta que resolve a movimentação resolve o ativo de uma sugestão de CADASTRO — cujo
 * `id_entidade_alvo` já é o id do ativo. Um endpoint, as duas categorias.
 */
export const indexarAtivos = (payload) => {
  const indice = {}
  const raiz = payload && typeof payload === 'object' ? payload : {}
  Object.keys(raiz).forEach((chave) => {
    const grupo = raiz[chave]
    // A CHAVE do payload e o `id_ativo_cliente`, e e o unico id garantido: `data` nem sempre traz
    // `id`, e quem resolve o ativo da sugestao compara por `asset.id`. Sem isto, "Editar" de uma
    // sugestao de cadastro na aba cai no ramo de erro.
    if (grupo && grupo.data) indice[String(chave)] = { ...grupo.data, id: chave }
  })
  return indice
}

export const entradaDaSugestao = ({ suggestion, indice } = {}) => {
  const alvo = suggestion && suggestion.id_entidade_alvo
  if (alvo === null || alvo === undefined || alvo === '') return null
  return (indice || {})[String(alvo)] || null
}

export const movimentacaoDaSugestao = (args) => {
  const entrada = entradaDaSugestao(args)
  return entrada ? entrada.movimentacao : null
}

export const ativoDaSugestao = (args) => {
  const entrada = entradaDaSugestao(args)
  return entrada ? entrada.ativo : null
}

/**
 * O que IDENTIFICA a movimentação dentro do ativo — sem repetir o nome do ativo, que agora é o
 * título do card. Repetir a identidade do pai na linha do filho é o que o benchmark de hierarquia
 * do design-system pede para não fazer.
 */
export const contextoDaMovimentacao = (mov) => {
  if (!mov) return ''
  const partes = [mov.operacao, mov.data].filter(Boolean)
  return partes.join(' de ')
}

export const movimentacaoComSugestao = ({ mov, suggestion } = {}) => {
  if (!mov) return null
  return { ...mov, ...patchDaSugestaoMov(suggestion) }
}
